import React from 'react';
import {
  Flame, AlertTriangle, Clock, CheckCircle2, Sparkles, DollarSign,
} from 'lucide-react';
import {
  bandFor, stageLabel,
  type Band, type DealScore, type Health, type Stage, type StrategyScore,
} from '../../data/dealHealth';

/**
 * Shared UI atoms for the deal-scoring surfaces.
 *
 * These are used in TWO places today:
 *   1. src/components/views/DealHealthView.tsx — the standalone preview
 *      served at /admin.html (a design lab, not a shipping route).
 *   2. src/components/views/AdminPortalView.tsx — the real admin surface;
 *      JV Submissions tab is the "Deals" list that gets these scores
 *      injected inline.
 *
 * Keeping them here means one visual language across both — a chip in
 * the preview and the same chip in the real portal are actually the
 * same component.
 */

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export function money(n: number, opts?: { compact?: boolean }): string {
  if (opts?.compact && n >= 1000) {
    return `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  }
  return `$${n.toLocaleString()}`;
}

export function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.max(0, Math.round(diff / 60_000));
  if (mins < 60) return `${mins}m ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

// ---------------------------------------------------------------------------
// Band colouring + tooltips
// ---------------------------------------------------------------------------

const BAND_TOOLTIP: Record<Band, string> = {
  hot:  'HOT (70–100) — strong candidate. High probability of closing. Contact immediately.',
  warm: 'WARM (40–69) — decent potential. Schedule follow-up this week.',
  cold: 'COLD (0–39) — low probability. Deprioritise unless pipeline is thin.',
};

function bandStyle(band: Band): { bg: string; text: string } {
  if (band === 'hot')  return { bg: 'bg-emerald-500', text: 'text-white' };
  if (band === 'warm') return { bg: 'bg-amber-500',   text: 'text-white' };
  return                     { bg: 'bg-gray-300',    text: 'text-gray-700' };
}

// ---------------------------------------------------------------------------
// Dual score pillar — both scores side-by-side, always visible
// ---------------------------------------------------------------------------

export function DualScorePillar({
  wholesale,
  rentback,
  recommended,
  size = 'md',
}: {
  wholesale: StrategyScore;
  rentback: StrategyScore;
  recommended: 'wholesale' | 'rentback' | 'either';
  size?: 'sm' | 'md' | 'lg';
}) {
  const dims =
    size === 'lg' ? 'w-14 h-14 text-xl' :
    size === 'sm' ? 'w-9  h-9  text-xs' :
                    'w-11 h-11 text-sm';
  const chip = (label: string, sc: StrategyScore, isRec: boolean) => {
    const s = bandStyle(sc.band);
    return (
      <div className="flex flex-col items-center gap-1">
        <div
          className={`${dims} ${s.bg} ${s.text} rounded-lg font-bold
                       flex items-center justify-center tabular-nums
                       shadow-sm ${isRec ? 'ring-2 ring-tdb-accent' : 'ring-1 ring-white'}`}
          title={`${label} score: ${sc.value}/100 · ${BAND_TOOLTIP[sc.band]}`}
        >
          {sc.value}
        </div>
        <span className={`text-[10px] uppercase tracking-wide font-semibold
                          ${isRec ? 'text-tdb-accent' : 'text-gray-500'}`}>
          {size === 'sm' ? label.charAt(0) : label}
        </span>
      </div>
    );
  };
  return (
    <div className="shrink-0 flex items-start gap-2">
      {chip('Wholesale', wholesale, recommended === 'wholesale')}
      {chip('Rentback',  rentback,  recommended === 'rentback')}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Health badge (SLA state)
// ---------------------------------------------------------------------------

const HEALTH_TOOLTIP: Record<Health, string> = {
  fresh:    'Fresh — lead contacted or submitted recently. SLA window is comfortable.',
  cooling:  'Cooling — SLA window is more than half elapsed. Follow up before it expires.',
  at_risk:  'SLA at risk — fewer than 25% of the SLA window remains. Prioritise now.',
  breached: 'SLA breached — new leads have a 72h window; existing conversations have 48h. Contact immediately.',
  engaged:  'Engaged — an admin has made first contact. Monitor for deal progression.',
  dead:     '',
};

export function HealthBadge({ health }: { health: Health }) {
  if (health === 'dead') return null;
  const map: Record<Health, { cls: string; Icon: typeof Flame; label: string }> = {
    fresh:    { cls: 'bg-blue-50 text-blue-700 border-blue-200',           Icon: Sparkles,       label: 'Fresh' },
    cooling:  { cls: 'bg-slate-50 text-slate-600 border-slate-200',        Icon: Clock,          label: 'Cooling' },
    at_risk:  { cls: 'bg-amber-50 text-amber-800 border-amber-300',        Icon: AlertTriangle,  label: 'SLA at risk' },
    breached: { cls: 'bg-red-50 text-red-800 border-red-300',              Icon: AlertTriangle,  label: 'SLA breached' },
    engaged:  { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',  Icon: CheckCircle2,   label: 'Engaged' },
    dead:     { cls: '',                                                    Icon: Clock,          label: '' },
  };
  const { cls, Icon, label } = map[health];
  return (
    <span
      title={HEALTH_TOOLTIP[health]}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs
                  font-semibold border ${cls}`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Flag chip (LLM-surfaced signals)
// ---------------------------------------------------------------------------

export function FlagChip({ label, tone }: { label: string; tone: 'positive' | 'concern' | 'info' }) {
  const cls =
    tone === 'positive' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
    tone === 'concern'  ? 'bg-red-50 text-red-700 border-red-200' :
                          'bg-slate-50 text-slate-600 border-slate-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px]
                      font-medium border ${cls}`}>
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Pipeline value hero — headline $ figure across the active pipeline
// ---------------------------------------------------------------------------

export function PipelineValueHero({
  total,
  dealCount,
  cutoffDate,
  byStage,
}: {
  total: number;
  dealCount: number;
  cutoffDate: string;
  byStage: Partial<Record<Stage, number>>;
}) {
  const cutoffPretty = new Date(cutoffDate).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
  return (
    <div className="bg-gradient-to-r from-tdb-primary via-tdb-secondary to-tdb-primary
                    rounded-2xl shadow-xl p-6 text-white mb-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-300 font-semibold
                        flex items-center gap-1.5">
            <DollarSign className="w-3.5 h-3.5" />
            Total potential deal value in the pipeline
          </p>
          <p className="text-5xl font-bold mt-2 tabular-nums">{money(total)}</p>
          <p className="text-sm text-gray-300 mt-2">
            Across <span className="text-white font-semibold">{dealCount}</span> active
            {' '}deals submitted since <span className="text-white font-semibold">{cutoffPretty}</span>
            {' · '}<span className="text-gray-400">excludes closed, dead, rejected, withdrawn and duplicate</span>
          </p>
        </div>

        <div className="flex flex-col gap-1 min-w-[220px]">
          {(['under_contract', 'contacted', 'reviewing', 'submitted'] as Stage[])
            .filter((s) => (byStage[s] ?? 0) > 0)
            .map((s) => {
              const v = byStage[s] ?? 0;
              const pct = total > 0 ? Math.round((v / total) * 100) : 0;
              return (
                <div key={s} className="flex items-center gap-3 text-sm">
                  <div className="w-32 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-tdb-accent rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-gray-300 w-28">{stageLabel(s)}</span>
                  <span className="text-white font-semibold tabular-nums">
                    {money(v, { compact: true })}
                  </span>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top-10 to call today — collapsible strip
// ---------------------------------------------------------------------------

export function TopTenPanel({
  deals,
  onOpen,
}: {
  deals: DealScore[];
  onOpen: (submissionId: string) => void;
}) {
  const [collapsed, setCollapsed] = React.useState(false);
  return (
    <div className="bg-white rounded-2xl shadow-lg p-5 mb-6 border border-gray-200">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-5 h-5 text-tdb-accent" />
        <h3 className="text-lg font-bold text-tdb-primary">Top 10 to call today</h3>
        <span className="text-xs text-gray-400 ml-1">Ranked by score × estimated fee</span>
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="ml-auto text-xs text-tdb-accent hover:underline font-medium"
        >
          {collapsed ? 'Show' : 'Hide'}
        </button>
      </div>
      {!collapsed && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {deals.map((d, i) => (
            <button
              key={d.submissionId}
              onClick={() => onOpen(d.submissionId)}
              className="text-left flex items-center gap-3 p-2 rounded-lg
                         hover:bg-gray-50 border border-transparent hover:border-gray-200
                         transition-colors"
            >
              <span className="text-xs font-mono text-gray-400 w-6 text-right tabular-nums">
                #{i + 1}
              </span>
              <DualScorePillar
                wholesale={d.scores.wholesale}
                rentback={d.scores.rentback}
                recommended={d.recommendedStrategy}
                size="sm"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {d.property.address}
                  <span className="text-gray-400 font-normal">
                    {' '}· {d.property.city}, {d.property.state}
                  </span>
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {d.submitter.displayName} · {money(d.estimatedWholesaleFee, { compact: true })} est. fee
                </p>
              </div>
              <HealthBadge health={d.health} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
